/**
 * Web NFC APIサービス（Android用）
 * AndroidデバイスのネイティブNFC機能を使用
 */

import {
  INFCService,
  NFCServiceEvent,
  NFCEventHandler,
  NFCCardData,
  encodeNFCData,
  decodeNFCData,
  validateUID,
  getOrCreateDeviceId,
} from './nfcServiceInterface';
import { showNotification } from '@mantine/notifications';

declare global {
  interface Window {
    NDEFReader: any;
  }
}

export class WebNFCService implements INFCService {
  private reader: any = null;
  private writer: any = null;
  private isConnectedFlag = false;
  private isWriteModeFlag = false;
  private handlers: Map<string, Set<NFCEventHandler>> = new Map();
  private deviceId: string;
  private abortController: AbortController | null = null;
  private recentlyWrittenCards: Set<string> = new Set();
  private scanActive = false;

  // Bound references for proper event listener removal
  private boundHandleReading: ((event: any) => Promise<void>) | null = null;
  private boundHandleReadingError: ((event: any) => void) | null = null;

  constructor() {
    this.deviceId = getOrCreateDeviceId();
    this.boundHandleReading = this.handleReading.bind(this);
    this.boundHandleReadingError = this.handleReadingError.bind(this);
  }

  async connect(): Promise<void> {
    if (this.isConnectedFlag) {
      return;
    }

    // Web NFC APIの可用性チェック
    if (!('NDEFReader' in window)) {
      const error = 'Web NFC API is not available';
      this.notifyHandlers({
        event: 'error',
        message: error,
      });
      throw new Error(error);
    }

    // HTTPS環境チェック
    if (!window.isSecureContext) {
      const error = 'Web NFC requires HTTPS connection';
      this.notifyHandlers({
        event: 'error',
        message: error,
      });
      throw new Error(error);
    }

    try {
      // NDEFReaderの初期化
      this.reader = new window.NDEFReader();
      this.writer = new window.NDEFReader();

      // 初回アクセス時にユーザージェスチャーなしで権限取得を試みる
      try {
        await this.startScanning();

        this.isConnectedFlag = true;

        // 接続成功を通知
        this.notifyHandlers({ event: 'connected' });

        showNotification({
          title: 'NFC自動スキャン開始',
          message: 'NFCカードをかざしてください',
          color: 'green',
          autoClose: 5000,
        });
      } catch (scanError: any) {
        // 自動スキャンが失敗した場合（権限が必要な場合）
        this.isConnectedFlag = true;
        this.notifyHandlers({ event: 'connected' });

        if (scanError.name === 'NotAllowedError') {
          // 権限が必要な場合は、最初のユーザーインタラクションで権限を取得
          showNotification({
            title: 'NFC権限が必要です',
            message: '画面をタップしてNFCを有効にしてください',
            color: 'orange',
            autoClose: false,
            id: 'nfc-permission-required',
          });

          // ページ全体にクリックリスナーを追加（最初のタップで権限取得）
          const handleFirstInteraction = async () => {
            try {
              await this.startManualScan();
              document.removeEventListener('click', handleFirstInteraction);
              showNotification({
                title: 'NFC有効',
                message: 'NFCスキャンが開始されました',
                color: 'green',
                id: 'nfc-permission-required',
              });
            } catch (_error) {
              // Failed to start scan on interaction - handled by startManualScan
            }
          };

          document.addEventListener('click', handleFirstInteraction);
        }
      }
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        this.notifyHandlers({
          event: 'permission_denied',
          message: 'NFC権限が拒否されました',
        });
        showNotification({
          title: 'NFC権限エラー',
          message: 'NFCの使用を許可してください',
          color: 'red',
        });
      } else {
        this.notifyHandlers({
          event: 'error',
          message: error.message,
        });
      }

      throw error;
    }
  }

  // 手動でスキャンを開始するパブリックメソッド
  async startManualScan(): Promise<void> {
    if (!this.isConnectedFlag) {
      throw new Error('Web NFC is not initialized. Call connect() first.');
    }

    if (this.scanActive) {
      return;
    }

    try {
      await this.startScanning();

      showNotification({
        title: 'NFCスキャン開始',
        message: 'NFCカードをかざしてください',
        color: 'green',
        autoClose: 3000,
      });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        showNotification({
          title: 'NFC権限エラー',
          message: 'NFCの使用を許可してください',
          color: 'red',
        });
        throw error;
      }

      showNotification({
        title: 'スキャンエラー',
        message: error.message,
        color: 'red',
      });
      throw error;
    }
  }

  private async startScanning(): Promise<void> {
    if (this.scanActive) {
      return;
    }

    try {
      // 新しいAbortControllerを作成
      this.abortController = new AbortController();

      // スキャンを開始
      await this.reader.scan({ signal: this.abortController.signal });
      this.scanActive = true;

      // 読み取りイベントのリスナーを設定（バインド済み参照を使用）
      this.reader.addEventListener('reading', this.boundHandleReading);
      this.reader.addEventListener('readingerror', this.boundHandleReadingError);
    } catch (error) {
      this.scanActive = false;
      throw error;
    }
  }

  private handleReading = async (event: any) => {
    // 書き込みモード中は読み取りをスキップ
    if (this.isWriteModeFlag) {
      return;
    }

    const { serialNumber, message } = event;
    const uid = this.formatUID(serialNumber);

    // 最近書き込まれたカードはスキップ
    if (this.recentlyWrittenCards.has(uid)) {
      return;
    }

    try {
      // メッセージからデータを読み取る
      let type = 0;
      let entityId = 0;

      if (message && message.records && message.records.length > 0) {
        for (const record of message.records) {
          // バイナリデータとして読み取り
          if (record.recordType === 'unknown' || record.recordType === 'application/octet-stream') {
            const data = new Uint8Array(record.data);
            if (data.length >= 4) {
              const decoded = decodeNFCData(data);
              type = decoded.type;
              entityId = decoded.entityId;
            }
          }
          // テキストデータとして読み取り（後方互換性）
          else if (record.recordType === 'text') {
            try {
              const textDecoder = new TextDecoder(record.encoding || 'utf-8');
              const text = textDecoder.decode(record.data);
              const parsed = JSON.parse(text);
              type = parsed.type || 0;
              entityId = parsed.entity_id || 0;
            } catch (_e) {
              // Failed to parse text record - skip
            }
          }
        }
      }

      // NFCカードデータを作成
      const cardData: NFCCardData = {
        uid,
        type,
        entity_id: entityId,
        timestamp: new Date().toISOString(),
      };

      // イベントを通知
      this.notifyHandlers({
        event: 'nfc_detected',
        data: cardData,
      });
    } catch (_error) {
      this.notifyHandlers({
        event: 'error',
        message: 'NFCタグの読み取りに失敗しました',
      });
    }
  };

  private handleReadingError = (_event: any) => {
    this.notifyHandlers({
      event: 'error',
      message: 'NFCタグの読み取りエラー',
    });
  };

  private formatUID(serialNumber: string): string {
    // シリアル番号を16進数文字列に変換
    return serialNumber
      .split(':')
      .join('')
      .toUpperCase()
      .padStart(14, '0');
  }

  async requestWriteCard(type: number, entityId: number): Promise<void> {
    if (!this.isConnectedFlag || !this.writer) {
      throw new Error('Web NFC is not connected');
    }

    this.isWriteModeFlag = true;

    try {
      // バイナリデータをエンコード
      const binaryData = encodeNFCData(type, entityId);

      // NDEFメッセージを作成
      const records = [
        {
          recordType: 'application/octet-stream',
          data: binaryData,
        },
        // 後方互換性のためテキストレコードも追加
        {
          recordType: 'text',
          data: new TextEncoder().encode(JSON.stringify({
            type,
            entity_id: entityId,
            issued_at: new Date().toISOString(),
          })),
        },
      ];

      showNotification({
        title: 'NFCカード書き込み',
        message: 'NFCカードをかざしてください',
        color: 'blue',
        autoClose: false,
        id: 'nfc-write-notification',
      });

      // カードへの書き込み
      await this.writer.write({ records });

      // 書き込み成功を通知
      this.notifyHandlers({
        event: 'card_written',
        data: {
          type,
          entity_id: entityId,
          timestamp: new Date().toISOString(),
        },
      });

      showNotification({
        title: '書き込み完了',
        message: 'NFCカードへの書き込みが完了しました',
        color: 'green',
        autoClose: 3000,
        id: 'nfc-write-notification',
      });

      // 書き込み後10秒間は同じカードの読み取りをスキップ
      const uid = `written_${Date.now()}`;
      this.recentlyWrittenCards.add(uid);
      setTimeout(() => {
        this.recentlyWrittenCards.delete(uid);
      }, 10000);
    } catch (error: any) {
      this.notifyHandlers({
        event: 'error',
        message: error.message,
        context: 'write',
      });

      showNotification({
        title: '書き込みエラー',
        message: 'NFCカードへの書き込みに失敗しました',
        color: 'red',
        id: 'nfc-write-notification',
      });

      throw error;
    } finally {
      // 書き込みモードを解除
      this.isWriteModeFlag = false;
    }
  }

  disconnect(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.reader) {
      this.reader.removeEventListener('reading', this.boundHandleReading);
      this.reader.removeEventListener('readingerror', this.boundHandleReadingError);
      this.reader = null;
    }

    this.writer = null;
    this.scanActive = false;
    this.isConnectedFlag = false;
    this.isWriteModeFlag = false;
    this.recentlyWrittenCards.clear();

    this.notifyHandlers({ event: 'disconnected' });
  }

  isConnected(): boolean {
    return this.isConnectedFlag;
  }

  isWriteMode(): boolean {
    return this.isWriteModeFlag;
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  getServiceType(): 'websocket' | 'web-nfc' | 'mock' {
    return 'web-nfc';
  }

  on(event: string, handler: NFCEventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // アンサブスクライブ関数を返す
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  off(event: string): void {
    this.handlers.delete(event);
  }

  clear(): void {
    this.handlers.clear();
  }

  private notifyHandlers(event: NFCServiceEvent): void {
    // 特定のイベントハンドラを実行
    const eventHandlers = this.handlers.get(event.event);
    if (eventHandlers) {
      eventHandlers.forEach((handler) => {
        try {
          handler(event);
        } catch (_error) {
          // Event handler error - silently ignore
        }
      });
    }

    // ワイルドカードハンドラを実行
    const wildcardHandlers = this.handlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => handler(event));
    }
  }
}
